import {
  datamuseQuery,
  isName,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Rhyme Finder's three questions and its filters.
 *
 * Datamuse answers all three from the CMU Pronouncing Dictionary:
 *
 * - `rel_rhy` — perfect rhymes: the same stressed vowel and everything after it
 *   (*time* / *climb* / *paradigm*).
 * - `rel_nry` — approximate rhymes, close but not exact (*forest* / *modest*).
 * - `sl` — "sounds like": words that sound similar overall, rhyme or not
 *   (*time* / *tame*), which is what puns and mnemonics want.
 *
 * `md=psf` adds each word's parts of speech, syllable count and frequency in Google
 * Books, which is what the name, syllable and rare-word filters read. The CMU
 * dictionary is full of surnames and place names (*Durkheim*, *Anaheim*), which a
 * poet rarely wants and which the name filter hides by default.
 */

const MAX_PERFECT = 300;
const MAX_NEAR = 150;
const MAX_SOUND = 60;

/** Below this many occurrences per million words, a word counts as rare. */
export const RARE_BELOW = 0.05;

export function rhymeQueries(word: string): [string, string, string] {
  return [
    datamuseQuery({ rel_rhy: word, md: "psf", max: MAX_PERFECT }),
    datamuseQuery({ rel_nry: word, md: "psf", max: MAX_NEAR }),
    datamuseQuery({ sl: word, md: "psf", max: MAX_SOUND }),
  ];
}

/** `"4"` stands for four or more. */
export type SyllableFilter = "any" | "1" | "2" | "3" | "4";

export const SYLLABLE_FILTERS: readonly SyllableFilter[] = [
  "any",
  "1",
  "2",
  "3",
  "4",
];

export interface RhymeOptions {
  syllables: SyllableFilter;
  /** Keep multi-word expressions such as "partner in crime". */
  phrases: boolean;
  /** Keep words below `RARE_BELOW` per million. */
  rare: boolean;
  /** Keep proper names (see `isName`). */
  names: boolean;
}

export function matchesSyllables(
  count: number | null,
  filter: SyllableFilter
): boolean {
  if (filter === "any") {
    return true;
  }
  if (count === null) {
    return false;
  }
  return filter === "4" ? count >= 4 : count === Number(filter);
}

export function isPhrase(word: string): boolean {
  return word.includes(" ");
}

export function isRare(result: Pick<WordResult, "frequency">): boolean {
  return result.frequency !== null && result.frequency < RARE_BELOW;
}

/** Apply the filters, dropping the searched word itself and any repeats. */
export function filterRhymes(
  word: string,
  results: readonly WordResult[],
  options: RhymeOptions
): WordResult[] {
  const searched = word.toLowerCase();
  const seen = new Set<string>();
  return results.filter((result) => {
    const key = result.word.toLowerCase();
    if (key === searched || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return (
      (options.phrases || !isPhrase(result.word)) &&
      (options.rare || !isRare(result)) &&
      (options.names || !isName(result)) &&
      matchesSyllables(result.syllables, options.syllables)
    );
  });
}

export interface SyllableGroup {
  /** Null for the few words Datamuse gives no syllable count. */
  syllables: number | null;
  words: string[];
}

/** One group per syllable count, fewest first; within a group, Datamuse's order. */
export function groupBySyllables(
  results: readonly WordResult[]
): SyllableGroup[] {
  const groups = new Map<number | null, string[]>();
  for (const result of results) {
    const list = groups.get(result.syllables) ?? [];
    list.push(result.word);
    groups.set(result.syllables, list);
  }
  return [...groups.entries()]
    .map(([syllables, words]) => ({ syllables, words }))
    .sort((a, b) => (a.syllables ?? 99) - (b.syllables ?? 99));
}
